import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { useGetProductsQuery } from "../../redux/api/productsApi";
import ProductCard from "../product/ProductCard";
import { setCartItem } from "../../redux/slices/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { openCart } from "../../redux/slices/uiSlice";
import { Helmet } from "react-helmet-async";
import { ChevronDown, ChevronRight, LayoutGrid, LayoutList } from "lucide-react";

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.12 },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const dividerVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.6, ease: "easeOut", delay: 0.25 },
  },
};

const SupportUkraine = () => {
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || 1);
  const keyword = searchParams.get("keyword") || "";

  const cartItems = useSelector((state) => state.cart.cartItems);
  const dispatch = useDispatch();

  const [sortOrder, setSortOrder] = useState("newest");
  const [layout, setLayout] = useState("grid");

  const { data, error, isError } = useGetProductsQuery({ page, keyword });
  const canvasRef = useRef(null);

  useEffect(() => {
    if (isError) {
      toast.error(
        error?.data?.message || error?.error || "Failed to load products"
      );
    }
  }, [isError, error]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let frame = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const sortedProducts = useMemo(() => {
    return (data?.products ?? [])
      .filter(
        (p) =>
          p.category === "Art For Ukraine" ||
          p.category === "String Art For Ukraine"
      )
      .sort((a, b) => {
        if (sortOrder === "newest")
          return new Date(b.createdAt) - new Date(a.createdAt);
        if (sortOrder === "priceLowHigh") return a.price - b.price;
        if (sortOrder === "priceHighLow") return b.price - a.price;
        return 0;
      });
  }, [data?.products, sortOrder]);

  const addItemToCart = (product) => {
    const existingCartItem = cartItems.find(
      (item) => item.product === product._id
    );

    if (existingCartItem && existingCartItem.quantity >= product.stock) {
      toast.error("Maximum items already added");
      return;
    }

    const newQuantity = existingCartItem ? existingCartItem.quantity + 1 : 1;

    dispatch(
      setCartItem({
        product: product?._id,
        name: product?.name,
        price: product?.price,
        image: product?.images[0]?.url,
        stock: product?.stock,
        quantity: newQuantity,
      })
    );

    dispatch(openCart());
    toast.success(existingCartItem ? "Quantity updated" : "Added to bag");
  };

  return (
    <>
      <Helmet>
        <title>Handmade Cards Supporting Ukraine — Sanguine Accent</title>
        <meta
          name="description"
          content="Handmade string art greeting cards, made by hand. 70% goes directly to people I know serving in Ukraine — for food, warm clothes, and communication devices."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sanguineaccent.com/supportUkraine" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Handmade String Art Cards Supporting Ukraine",
            description:
              "Handmade string art greeting cards — 70% of every purchase goes directly to people serving in Ukraine for food, clothing, and communication devices.",
            url: "https://sanguineaccent.com/supportUkraine",
            hasPart: sortedProducts.map((product) => ({
              "@type": "Product",
              name: product.name,
              image: product.images[0]?.url,
              url: `https://sanguineaccent.com/products/${product._id}`,
              offers: {
                "@type": "Offer",
                priceCurrency: "USD",
                price: product.price,
                availability:
                  product.stock > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
              },
            })),
          })}
        </script>
      </Helmet>

      <main className="font-sourcesans">
        {/* Breadcrumb */}
        <div className="pt-28 md:pt-44 py-2 px-4">
          <nav aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-2">
              <li className="inline-flex items-center">
                <Link
                  to="/"
                  className="text-sm font-medium text-stone-700 hover:text-[#BC3F4A]"
                >
                  Home
                </Link>
              </li>
              <li className="flex items-center">
                <ChevronRight className="w-3 h-3 text-stone-400 mx-1" aria-hidden="true" />
                <span className="text-sm font-medium text-stone-500">
                  Art for Ukraine
                </span>
              </li>
            </ol>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative w-full h-48 md:h-64 lg:h-80" aria-hidden="true">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://res.cloudinary.com/ducc0wskb/image/upload/v1778277632/bg-sa_tym8yw.webp')",
            }}
          />
        </section>

        {/* Mission */}
        <section className="relative bg-[#f5f3ea] py-12 px-8 md:px-12 border-t border-stone-200 overflow-hidden">

          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />

          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
          />

          <motion.div
            className="relative z-10 max-w-3xl mx-auto text-center font-sourcesans"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={sectionVariants}
          >
            <motion.p
              className="text-[10px] uppercase tracking-[0.25em] text-stone-400 mb-4"
              variants={childVariants}
            >
              Art for Ukraine
            </motion.p>

            <motion.h1
              className="text-2xl md:text-3xl font-playfair font-medium text-stone-800 leading-snug"
              variants={childVariants}
              style={{ textWrap: "balance" }}
            >
              Each card is{" "}
              <span className="text-[#BC3F4A] italic">made by hand.</span>
            </motion.h1>

            {/* 70% — matches FAQ */}
            <motion.p
              className="mt-4 text-base md:text-lg text-stone-700 leading-relaxed max-w-xl mx-auto"
              variants={childVariants}
              style={{ textWrap: "balance" }}
            >
              70% of your purchase goes directly to trusted individuals in
              Ukraine — for food, warm clothes, vehicle repair, and communication
              devices.
            </motion.p>

            {/* 30% — matches FAQ exactly */}
            <motion.p
              className="mt-3 text-sm text-stone-500 leading-relaxed max-w-lg mx-auto"
              variants={childVariants}
              style={{ textWrap: "balance" }}
            >
              The remaining 30% covers website hosting, transaction fees,
              materials, and helps us continue creating and bringing you more
              products.
            </motion.p>

            <motion.p
              className="mt-3 text-sm text-stone-500 leading-relaxed max-w-lg mx-auto"
              variants={childVariants}
              style={{ textWrap: "balance" }}
            >
              These are personal transfers to individuals I know — not a charity
              or nonprofit. Every purchase makes a direct, traceable difference.
            </motion.p>

            <motion.div
              className="mt-6 w-12 h-px bg-[#BC3F4A]/40 mx-auto"
              variants={dividerVariants}
              style={{ originX: 0.5 }}
            />

            <motion.p
              className="mt-4 text-[10px] uppercase tracking-[0.2em] text-stone-400"
              variants={childVariants}
            >
              Make it. Mean it.
            </motion.p>
          </motion.div>

          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
        </section>

        {/* Controls + Products */}
        <section
          className="bg-[#f5f3ea] py-4 px-2 md:px-12 border-t border-stone-200"
          aria-label="Products"
        >
          <div className="flex items-center justify-between mb-6 gap-4">
            <div className="relative inline-block">
              <label className="sr-only" htmlFor="sort-ukraine">Sort by</label>
              <select
                id="sort-ukraine"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="appearance-none px-2 py-1 pr-8 text-stone-700 bg-transparent text-sm focus:outline-none focus:ring-0"
              >
                <option value="newest">Newest</option>
                <option value="priceLowHigh">Price: Low to High</option>
                <option value="priceHighLow">Price: High to Low</option>
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown className="w-4 h-4 text-stone-700" />
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setLayout("grid")}
                className={`p-2 text-stone-800 ${layout === "grid" ? "bg-stone-200 rounded-full" : ""}`}
                aria-label="Grid layout"
                aria-pressed={layout === "grid"}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setLayout("list")}
                className={`p-2 text-stone-800 ${layout === "list" ? "bg-stone-200 rounded-full" : ""}`}
                aria-label="List layout"
                aria-pressed={layout === "list"}
              >
                <LayoutList className="w-4 h-4" />
              </button>
            </div>
          </div>

          {sortedProducts.length > 0 ? (
            <div
              className={
                layout === "grid"
                  ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6"
                  : "flex flex-col gap-6"
              }
            >
              {sortedProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  addItemToCart={addItemToCart}
                  layout={layout}
                  highRes={layout === "list"}
                />
              ))}
            </div>
          ) : (
            <p className="text-stone-500 font-sourcesans text-center mt-10">
              No products available yet.
            </p>
          )}
        </section>
      </main>
    </>
  );
};

export default SupportUkraine;